import React, { MouseEvent, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { MenuItem } from "@shared/interfaces";
import { AccessControl } from "@shared/components/AccessControl";
import { isUserContractor } from "@shared/utils";
import { selectors as sharedSelectors } from "@shared/store";
import { NameOfChildRoutes } from "@shared/constants";
import classnames from "classnames";

import { Tooltip } from "../../../Tooltip";

import "./index.scss";

interface SidebarItemProps {
  item: MenuItem;
  currentPathname: string;
}

const SidebarItem = (props: SidebarItemProps) => {
  const { item, currentPathname } = props;
  const { path, permission } = item;

  const authenticatedUser = useSelector(sharedSelectors.getUserDetails());
  const isCurrentUserContractor = isUserContractor(authenticatedUser ? authenticatedUser : undefined);

  const directPath = useMemo(() => {
    if (isCurrentUserContractor && path === NameOfChildRoutes.PROCEDURES.ROOT) {
      return `${NameOfChildRoutes.PROCEDURES.ROOT}${NameOfChildRoutes.PROCEDURES.PROCESSES}`;
    }

    return path;
  }, [path, isCurrentUserContractor]);

  const isActive = useMemo(() => {
    const isExcludePath = item.exclude_paths?.find((ep: string) => currentPathname.includes(ep));
    return currentPathname.includes(item.root_path) && !isExcludePath;
  }, [currentPathname, item.root_path, item.exclude_paths]);

  const handleNavLinkClick = useCallback(
    (event: MouseEvent) => {
      if (item.onClick) {
        item.onClick();
        event.preventDefault();
        (event.currentTarget as HTMLElement).blur();
      }
    },
    [item],
  );

  // sidebar.tsx keeps a deliberate trailing space on the second "Procedures" entry's `name` so the
  // two rows keep distinct React list keys (`SidebarItems.tsx`'s `key={item.name}`) -- trimming it
  // there would collide both entries onto the same key. Trimmed only here, where it becomes visible
  // text (the tooltip and the link's accessible name).
  const itemName = item.name.trim();

  return (
    <AccessControl permission={permission}>
      <div className={classnames("sidebar-item", item.onClick ? "schema" : "")}>
        {/* block: the NavLink is `display: flex; height: 64px` and expected to fill the 80px-wide
            rail -- the anchor span Tooltip inserts must be block-level too, or the row's own width
            collapses to the NavLink's (empty) content size instead. */}
        <Tooltip text={itemName} placement="right" block>
          <NavLink
            to={directPath}
            onClick={handleNavLinkClick}
            end={false}
            className={isActive ? "active" : undefined}
            aria-label={itemName}
          >
            <div className="item-icon">
              <item.icon />
            </div>
          </NavLink>
        </Tooltip>
      </div>
    </AccessControl>
  );
};

export default SidebarItem;
